import { Link } from '@remix-run/react';
import React, { useEffect, useRef, useState } from 'react';
import tinycolor from 'tinycolor2';
import { DARK_THEME_CLASSNAME, isDarkSystemTheme } from '~/core/theming';
import ConditionalWrapper from './ConditionalWrapper';

interface FancyButtonProps {
  className?: string;
  containerClassName?: string;
  canvasClassName?: string;
  text?: string;
  color?: string;
  borderRadius?: string;
  dotAmount?: number;
  keepDotsInside?: boolean;
  href?: string;
  newTab?: boolean;
  icon?: React.ReactNode;
  variant?: 'filled' | 'outline';
  animateStars?: boolean;
  showStars?: boolean;
  forceWhite?: boolean;
  scale?: number;
  children?: React.ReactNode;
  utmSource?: string;
  forceHoverState?: boolean;
  textShadow?: boolean;
  onClick?: () => void;
}

const FancyButton: React.FC<FancyButtonProps> = ({
  text,
  color = '#f59e0b',
  className = '',
  containerClassName = '',
  canvasClassName = '',
  borderRadius = '29px',
  dotAmount = 40,
  href,
  newTab = false,
  keepDotsInside = false,
  icon,
  scale = 1.1,
  variant = 'filled',
  forceWhite = false,
  showStars = false,
  animateStars = true,
  children,
  utmSource,
  forceHoverState = false,
  textShadow = true,
  onClick = () => {},
}) => {
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [textColor, setTextColor] = useState(
    tinycolor(color).isDark() ? '#ffffff' : '#000000',
  );
  const [dotColor, setDotColor] = useState(
    tinycolor(color).lighten(25).toString(),
  );
  const [ready, setReady] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [buttonId] = useState(
    `fancy-button-${Math.random().toString(36).substring(2, 9)}`,
  );
  const containerRef = useRef<HTMLAnchorElement | HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<SVGSVGElement>(null);

  const backgroundColor = color;
  const borderColor = tinycolor(color).lighten(40).toString();

  useEffect(() => {
    // check the html element for the dark theme class
    if (typeof window === 'undefined') return;

    const checkTheme = () => {
      if (document.documentElement.classList.contains(DARK_THEME_CLASSNAME)) {
        setIsDarkTheme(true);
        setTextColor('#ffffff');
        setDotColor(tinycolor(color).lighten(25).toString());
      } else {
        setIsDarkTheme(isDarkSystemTheme());
        if (forceWhite) {
          setTextColor('#ffffff');
          setDotColor(tinycolor(color).lighten(5).toString());
        } else {
          setTextColor('#000000');
          setDotColor(tinycolor(color).lighten(5).toString());
        }
      }
    };

    // Initialize theme
    checkTheme();

    // Observe changes to the class attribute
    const observer = new MutationObserver((mutationsList) => {
      for (let mutation of mutationsList) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          checkTheme();
        }
      }
    });

    observer.observe(document.documentElement, { attributes: true });

    // Clean up observer on component unmount
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;

    if (!container || !canvas) return;

    const { width, height } = container.getBoundingClientRect();
    const canvasWidth = width + 100;
    const canvasHeight = height + 100;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let particles: {
      x: number;
      y: number;
      radius: number;
      opacity: number;
      angle: number;
      angularSpeed: number;
      centerX: number;
      centerY: number;
    }[] = [];

    let snakeParticles: {
      x: number;
      y: number;
      length: number;
      width: number;
      opacity: number;
      angle: number;
      speed: number;
      amplitude: number;
      segments: { x: number; y: number }[];
    }[] = [];

    const createParticles = () => {
      particles = [];
      for (let i = 0; i < dotAmount * 0.8; i++) {
        const x = Math.random() * canvasWidth;
        const y = Math.random() * canvasHeight;
        const radius = Math.random() * 1.5 + 0.25;
        const opacity = Math.random() * 0.5 + 0.25;
        const angle = Math.random() * Math.PI * 2;
        const angularSpeed = Math.random() * 0.0025 + 0.0005;
        const centerX = x;
        const centerY = y;

        particles.push({
          x,
          y,
          radius,
          opacity,
          angle,
          angularSpeed,
          centerX,
          centerY,
        });
      }

      snakeParticles = [];
      for (let i = 0; i < dotAmount * 0.2; i++) {
        const x = Math.random() * (canvasWidth / 2);
        const y = Math.random() * canvasHeight;
        const length = Math.random() * 21 + 7;
        const width = Math.random() * 1 + 0.25;
        const opacity = Math.random() * 0.5 + 0.25;
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 0.5 + 0.1;
        const amplitude = Math.random() * 5 + 2;
        const segments = [];

        for (let j = 0; j < length; j++) {
          segments.push({ x, y });
        }

        snakeParticles.push({
          x,
          y,
          length,
          width,
          opacity,
          angle,
          speed,
          amplitude,
          segments,
        });
      }
    };

    const renderLoop = () => {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      particles.forEach((particle) => {
        const angle =
          particle.angle + performance.now() * particle.angularSpeed;

        particle.x =
          particle.centerX +
          Math.cos(angle) * particle.radius * (canvasWidth / 7);
        particle.y =
          particle.centerY +
          Math.sin(angle) * particle.radius * (canvasHeight / 7);

        const mouseDistance = Math.sqrt(
          Math.pow(particle.x - mouseX, 2) + Math.pow(particle.y - mouseY, 2),
        );
        const mouseThreshold = 100;

        if (mouseDistance < mouseThreshold) {
          const repelStrength =
            (mouseThreshold - mouseDistance) / mouseThreshold;
          const repelAngle = Math.atan2(
            particle.y - mouseY,
            particle.x - mouseX,
          );
          particle.x += Math.cos(repelAngle) * repelStrength * 17.7;
          particle.y += Math.sin(repelAngle) * repelStrength * 17.7;
        }

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${tinycolor(dotColor).toRgb().r}, ${
          tinycolor(dotColor).toRgb().g
        }, ${tinycolor(dotColor).toRgb().b}, ${particle.opacity})`;
        ctx.fill();
        ctx.closePath();
      });

      snakeParticles.forEach((particle) => {
        particle.x += Math.cos(particle.angle) * particle.speed;
        particle.y += Math.sin(particle.angle) * particle.speed;
        particle.angle += Math.sin(performance.now() * 0.001) * 0.05;

        const mouseDistance = Math.sqrt(
          Math.pow(particle.x - mouseX, 2) + Math.pow(particle.y - mouseY, 2),
        );
        const mouseThreshold = 100;

        if (mouseDistance < mouseThreshold) {
          const repelStrength =
            (mouseThreshold - mouseDistance) / mouseThreshold;
          const repelAngle = Math.atan2(
            particle.y - mouseY,
            particle.x - mouseX,
          );
          particle.x += Math.cos(repelAngle) * repelStrength * 17.7;
          particle.y += Math.sin(repelAngle) * repelStrength * 17.7;
        }

        particle.segments.unshift({ x: particle.x, y: particle.y });
        particle.segments = particle.segments.slice(0, particle.length);

        ctx.beginPath();
        ctx.moveTo(particle.segments[0].x, particle.segments[0].y);
        for (let i = 1; i < particle.segments.length; i++) {
          const segment = particle.segments[i];
          const prevSegment = particle.segments[i - 1];
          const midX = (segment.x + prevSegment.x) / 2;
          const midY = (segment.y + prevSegment.y) / 2;
          ctx.quadraticCurveTo(prevSegment.x, prevSegment.y, midX, midY);
        }
        ctx.strokeStyle = `rgba(${tinycolor(dotColor).toRgb().r}, ${
          tinycolor(dotColor).toRgb().g
        }, ${tinycolor(dotColor).toRgb().b}, ${particle.opacity})`;
        ctx.lineWidth = particle.width;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.closePath();
      });

      if (particles.length > 0 || snakeParticles.length > 0) {
        requestAnimationFrame(renderLoop);
      }
    };

    const handleInteractionStart = () => {
      setHovered(true);
      createParticles();
      requestAnimationFrame(renderLoop);
    };

    const handleInteractionEnd = () => {
      setHovered(false);
      particles = [];
      snakeParticles = [];

      const stars = starsRef.current;
      if (!stars) return;

      stars.style.setProperty('--generate-button-star-1-scale', '1');
      stars.style.setProperty('--generate-button-star-1-opacity', '1');
      stars.style.setProperty('--generate-button-star-2-scale', '1');
      stars.style.setProperty('--generate-button-star-2-opacity', '1');
      stars.style.setProperty('--generate-button-star-3-scale', '1');
      stars.style.setProperty('--generate-button-star-3-opacity', '1');
    };

    // Add event listeners for mouse movement
    let mouseX = 0;
    let mouseY = 0;

    const handleMouseMove = (event: MouseEvent) => {
      mouseX = event.clientX - container.getBoundingClientRect().left;
      mouseY = event.clientY - container.getBoundingClientRect().top;
    };

    const handleTouchMove = (event: TouchEvent) => {
      mouseX =
        event.touches[0].clientX - container.getBoundingClientRect().left;
      mouseY = event.touches[0].clientY - container.getBoundingClientRect().top;
    };

    if (forceHoverState) {
      handleInteractionStart();
    }

    container.addEventListener('mousemove', handleMouseMove as EventListener);
    container.addEventListener('touchmove', handleTouchMove as EventListener);
    container.addEventListener('mouseenter', handleInteractionStart);
    container.addEventListener('mouseleave', handleInteractionEnd);
    container.addEventListener('touchstart', handleInteractionStart);
    container.addEventListener('touchend', handleInteractionEnd);

    return () => {
      container.removeEventListener(
        'mousemove',
        handleMouseMove as EventListener,
      );
      container.removeEventListener('mouseenter', handleInteractionStart);
      container.removeEventListener('mouseleave', handleInteractionEnd);
      container.removeEventListener('touchstart', handleInteractionStart);
      container.removeEventListener('touchend', handleInteractionEnd);
    };
  }, [
    dotAmount,
    dotColor,
    ready,
    keepDotsInside,
    animateStars,
    forceHoverState,
  ]);

  const buttonVariantStyle = {
    filled: {
      backgroundColor:
        hovered || forceHoverState
          ? tinycolor(color).darken(0).toHexString()
          : backgroundColor,
      color: tinycolor(color).isDark() ? '#ffffff' : '#000000',
    },
    outline: {
      backgroundColor:
        hovered || forceHoverState ? backgroundColor : 'transparent',
      color:
        hovered || forceHoverState
          ? tinycolor(color).isDark()
            ? '#ffffff'
            : '#000000'
          : color,
      border: `2px solid ${color}`,
    },
  };

  return (
    <ConditionalWrapper
      if={href !== undefined}
      componentA={Link}
      componentB="button"
      to={
        href
          ? `${href}${text ? `?utm_source=${text}` : ''}${utmSource ? `&utm_medium=button&utm_campaign=${utmSource}` : ''}`
          : undefined
      }
      ref={containerRef}
      className={`relative z-50 ${buttonId}-container ${containerClassName}`}
      onClick={onClick}
      tabIndex={0}
      target={href && newTab ? '_blank' : undefined}
    >
      <canvas
        ref={canvasRef}
        className={
          'dots-container h-[calc(100%+50px)] w-[calc(100%+100px)] max-w-[calc(100%+100px)] md:max-w-lg ' +
          canvasClassName
        }
      />
      <style>
        {`
          .${buttonId}-container {
            position: relative;
            display: inline-block;
          }

          .dots-container {
            position: absolute;
            top: -50px;
            left: -50px;
            right: -50px;
            bottom: -50px;
            pointer-events: none; /* Allow mouse events to pass through to the button */
            z-index: 7;
            margin: 0 auto;
          }

          .${buttonId} {
            --button-color: ${tinycolor(color).isDark() ? '#ffffff' : '#000000'};
            --button-background: ${backgroundColor};
            --button-border-color: ${borderColor};
            --button-border-radius: ${borderRadius};
            --button-dot-color: ${dotColor};
            --button-shadow-color: ${tinycolor(color).setAlpha(0.2).toRgbString()};
            --button-shadow-inset-color: ${tinycolor(color).lighten(70).setAlpha(0.35).toRgbString()};
            --button-shadow-outline-color: ${tinycolor(color).setAlpha(0.4).toRgbString()};
            --button-hover-background: ${tinycolor(color).darken(0).toHexString()};
            --button-hover-color: ${textColor};
            
            appearance: none;
            outline: none;
            border: none;
            padding: 16px 24px 16px 20px;
            border-radius: var(--button-border-radius);
            margin: 0;
            background-color: ${buttonVariantStyle[variant].backgroundColor};
            color: ${buttonVariantStyle[variant].color};
            ${variant === 'outline' ? `border: ${buttonVariantStyle[variant].border};` : ''}
            opacity: 1; ${hovered ? 1 : 0.7};
            min-width: 192px;
            display: flex;
            position: relative;
            font-family: 'Inter', sans-serif;
            font-weight: 600;
            font-size: 16px;
            line-height: 26px;
            letter-spacing: 0.005em;
            cursor: pointer;
            -webkit-tap-highlight-color: transparent;
            z-index: 1;
            transform: scale(var(--button-scale, 1)) translateZ(0);
            transition: transform .3s cubic-bezier(0.2, 0.6, 0.3, 1), color .3s cubic-bezier(0.2, 0.6, 0.3, 1), background-color .3s cubic-bezier(0.2, 0.6, 0.3, 1), box-shadow .3s cubic-bezier(0.2, 0.6, 0.3, 1);
            box-shadow: 0 0 0 0 var(--button-shadow-color);
          }

          .${buttonId} .icon-right {
            margin-left: 8px;
          }

          .${buttonId}:hover,
          .${buttonId}.force-hover {
            --button-scale: ${scale};
            --button-shadow-wide: ${tinycolor(color).lighten(40).setAlpha(0.4).toRgbString()};
            --button-shadow-inset: var(--button-shadow-inset-color);
            --button-shadow-outline: 3px;
            color: var(--button-hover-color);
            background-color: var(--button-hover-background);
            box-shadow: 0px 0px 120px var(--button-shadow-color),
                      0px 4px 12px rgba(0, 0, 0, 0.05),
                      0px 1px 2px rgba(0, 0, 0, 0.1),
                      inset 0px 1px 1px var(--button-shadow-inset-color);
          }

          .${buttonId}:hover:before,
          .${buttonId}.force-hover:before {
            opacity: 1;
          }

          .${buttonId}:hover span:before,
          .${buttonId}.force-hover span:before {
            opacity: 0;
          }

          .${buttonId}:before {
            content: '';
            display: block;
            position: absolute;
            right: 20%;
            height: 20px;
            left: 20%;
            bottom: -10px;
            background: var(--button-border-color);
            filter: blur(12.5px);
            z-index: 1;
            clip-path: inset(-200% -30% 10px -30% round var(--button-border-radius));
            opacity: 0;
            transition: opacity .4s ease;
            transform: translateZ(0);
          }

          .${buttonId} span {
            position: relative;
            width: 100%;
            z-index: 1;
            text-align: center;
            /*display: block;*/
            ${showStars ? 'margin-right: .5rem;' : ''};
            /*color: ${textColor};*/
            ${(tinycolor(color).isLight() && variant !== 'outline' && textShadow) || (hovered && textShadow) ? 'filter: drop-shadow(2px 4px 7px #00000098)' : ''};
          }

          .${buttonId} span:before {
            /*content: ''; */
            background-image: linear-gradient(to right, transparent, var(--button-background));
            position: absolute;
            inset: 0;
            z-index: 1;
            pointer-events: none;
            opacity: .4;
            transition: opacity .4s ease;
          }

          .${buttonId}:active {
            --button-scale: 1.05;
          }

          .${buttonId}-container .dot {
            position: absolute;
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background-color: ${dotColor};
            /*background-color: var(--button-dot-color);*/
            opacity: 0;
            z-index: 2;
          }

          .${buttonId} svg.icon {
            width: 42px;
            height: 26px;
            display: flex;
            justify-content: center;
            margin: auto;        
            /*margin-right: 12px;*/
            fill: ${textColor};
            filter: drop-shadow(2px 4px 7px #00000095);   
            transform-origin: center;
          }

          .${buttonId} svg.icon path {
            transform-origin: center;
            transition: transform 0.3s ease, opacity 0.3s ease;
          }

          .${buttonId} .stroke {
            mix-blend-mode: hard-light;
          }
          .${buttonId}:hover .stroke svg,
          .${buttonId}.force-hover .stroke svg {
            -webkit-animation: stroke 2s linear infinite;
            animation: stroke 2s linear infinite;
          }
          .${buttonId} .stroke svg {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            fill: none;
            stroke-width: 0.75px;
            stroke: ${tinycolor(color).lighten(40).toHexString()};
            stroke-dasharray: 1.5 14;
            stroke-dashoffset: 22;
            opacity: 0;
          }
          .${buttonId} .stroke svg:nth-child(2) {
            stroke-width: 1px;
            stroke-opacity: 0.5;
            filter: blur(3px);
          }

          @keyframes stroke {
            0% {
              opacity: 0;
            }
            25%,
            75% {
              opacity: 1;
            }
            95%,
            100% {
              stroke-dashoffset: 6;
              opacity: 0;
            }
          }
        `}
      </style>
      <span
        className={`${buttonId} ${className} ${forceHoverState ? 'force-hover' : ''}`}
        style={
          {
            '--button-color': color,
            '--button-background': backgroundColor,
            '--button-border-color': borderColor,
            '--button-border-radius': borderRadius,
            '--button-dot-color': dotColor,
          } as React.CSSProperties
        }
      >
        {showStars && (
          <svg ref={starsRef} className="icon" viewBox="0 0 24 26">
            <path d="M5.16515 2.62145L5.8075 0.999247C5.83876 0.919722 5.9154 0.866699 6.00112 0.866699C6.08683 0.866699 6.16347 0.919722 6.19473 0.999247L6.83708 2.62145L8.44145 3.27094C8.5201 3.30254 8.57254 3.38003 8.57254 3.4667C8.57254 3.55337 8.5201 3.63085 8.44145 3.66246L6.83708 4.31195L6.19473 5.93415C6.16347 6.0147 6.08683 6.0667 6.00112 6.0667C5.9154 6.0667 5.83876 6.0147 5.8075 5.93415L5.16515 4.31195L3.56078 3.66246C3.48112 3.63085 3.42969 3.55337 3.42969 3.4667C3.42969 3.38003 3.48112 3.30254 3.56078 3.27094L5.16515 2.62145Z" />
            <path d="M11.2362 9.43967C11.5502 9.30067 11.8015 9.05025 11.9405 8.73617L13.5494 5.11725C13.7169 4.74204 14.0887 4.5 14.5 4.5C14.9112 4.5 15.2839 4.74204 15.4506 5.11725L17.0603 8.73617C17.1985 9.05025 17.4497 9.3015 17.7638 9.43967L21.3827 11.0494C21.7579 11.2161 22 11.5887 22 12C22 12.4112 21.7579 12.7831 21.3827 12.9506L17.7638 14.5595C17.4497 14.6985 17.1993 14.9497 17.0603 15.2638L15.4506 18.8827C15.2839 19.2579 14.9112 19.5 14.5 19.5C14.0887 19.5 13.7169 19.2579 13.5494 18.8827L11.9405 15.2638C11.8015 14.9497 11.5502 14.6985 11.2362 14.5595L7.61725 12.9506C7.24204 12.7831 7 12.4112 7 12C7 11.5887 7.24204 11.2161 7.61725 11.0494L11.2362 9.43967Z" />
            <path d="M4.60728 19.392L5.67703 16.6875C5.72997 16.5541 5.85854 16.4666 6.00056 16.4666C6.14258 16.4666 6.27031 16.5541 6.32325 16.6875L7.39299 19.392L10.0678 20.4736C10.1997 20.5271 10.2863 20.6563 10.2863 20.7999C10.2863 20.9435 10.1997 21.0735 10.0678 21.1271L7.39299 22.2087L6.32325 24.9123C6.27031 25.0457 6.14258 25.1332 6.00056 25.1332C5.85854 25.1332 5.72997 25.0457 5.67703 24.9123L4.60728 22.2087L1.93333 21.1271C1.8014 21.0735 1.71484 20.9435 1.71484 20.7999C1.71484 20.6563 1.8014 20.5271 1.93333 20.4736L4.60728 19.392Z" />
          </svg>
        )}
        <span>{text || children}</span>
        {icon && <div className="icon-right">{icon}</div>}
        <div className="stroke">
          <svg className="stroke-line" viewBox="0 0 195 58">
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              rx="29"
              ry="29"
              pathLength="10"
            />
          </svg>
          <svg className="stroke-line" viewBox="0 0 195 58">
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              rx="29"
              ry="29"
              pathLength="10"
            />
          </svg>
        </div>
      </span>
    </ConditionalWrapper>
  );
};

export default FancyButton;
